<script setup lang="ts">
import {computed, ref, watch} from 'vue';
import {ElCheckbox, ElInput, ElOption, ElSelect, ElSwitch, ElTable, ElTableColumn} from 'element-plus';
import {formatFte, FTE_BUCKETS, FteBucket, INITIATIEF_FASE_VOLGORDE, InitiatiefDetail, InitiatiefFaseEnum, InitiatiefFaseEnum_name, InitiatievenPage} from 'common';
import {dashboardInitiatieven} from '@/base/callBackend.js';
import {dashboardOrgs, dashboardPeriod, laadDashboard} from '@/base/dashboardState.js';
import KpiCard from '@/views/dashboard/KpiCard.vue';
import FaseVerdelingChart from '@/views/dashboard/FaseVerdelingChart.vue';
import FaseBoxplot from '@/views/dashboard/FaseBoxplot.vue';
import InitiatiefTimeline from '@/views/dashboard/InitiatiefTimeline.vue';

const MIN_SNAPSHOTS: number = 6;

const loading            = ref<boolean>(true);
const page               = ref<InitiatievenPage | undefined>(undefined);
const percentage         = ref<boolean>(false);
const filterOrg          = ref<string[]>([]);
const filterFase         = ref<InitiatiefFaseEnum[]>([]);
const filterBucket       = ref<FteBucket[]>([]);
const alleenUitschieters = ref<boolean>(false);
const zoek               = ref<string>('');
let loadSeq: number = 0;

const organisaties = computed<string[]>(() => [...new Set((page.value?.details ?? []).map(d => d.organisatie))].sort());
const details      = computed<InitiatiefDetail[]>(() => {
    const z: string = zoek.value.trim().toLowerCase();
    return (page.value?.details ?? []).filter(d =>
        (filterOrg.value.length === 0 || filterOrg.value.includes(d.organisatie))
        && (filterFase.value.length === 0 || filterFase.value.includes(d.fase))
        && (filterBucket.value.length === 0 || filterBucket.value.includes(d.bucket))
        && (!alleenUitschieters.value || d.uitschieter)
        && (z === '' || d.initiatief.toLowerCase().includes(z) || d.toelichting.toLowerCase().includes(z)),
    );
});

watch([dashboardPeriod, dashboardOrgs], load, {immediate: true});

async function load() {
    if (dashboardPeriod.value === '') {
        return;
    }
    const seq: number                          = ++loadSeq;
    loading.value                              = true;
    const result: InitiatievenPage | undefined = await laadDashboard(() => dashboardInitiatieven(dashboardPeriod.value, dashboardOrgs.value));
    if (seq !== loadSeq) {
        return;
    }
    page.value    = result;
    loading.value = false;
}

function deltaTekst(delta: number | undefined): string {
    return delta === undefined ? 'geen vorig kwartaal' : `${delta >= 0 ? '+' : ''}${delta} t.o.v. vorig kwartaal`;
}
</script>

<template>
    <div class="px-10 py-4 flex flex-col gap-8">
        <div v-if="loading" class="text-sm text-gray-500">laden...</div>
        <template v-else-if="page">
            <div class="flex flex-row flex-wrap gap-4">
                <KpiCard v-for="k in page.kaarten" :key="k.label" data-testid="initiatieven-card" :label="k.label" :value="`${k.waarde}`" :detail="deltaTekst(k.delta)"/>
            </div>

            <section class="flex flex-col gap-2">
                <div class="flex flex-row items-center gap-4">
                    <div class="text-xl font-semibold text-zzc-blue">Initiatieven per fase</div>
                    <el-switch v-model="percentage" active-text="% van FTE-target tonen"/>
                </div>
                <FaseVerdelingChart :verdeling="page.verdeling" :percentage="percentage"/>
            </section>

            <section class="flex flex-col gap-2">
                <div class="text-xl font-semibold text-zzc-blue">Verblijftijd per fase</div>
                <div class="text-sm text-gray-500">Spreiding en uitschieters in aantal kwartalen per fase, berekend over alle organisaties.</div>
                <div v-if="page.snapshots < MIN_SNAPSHOTS" class="text-sm text-orange-600">Er zijn minder dan zes snapshots beschikbaar; medianen en uitschieters zijn daarom indicatief.</div>
                <FaseBoxplot :statistiek="page.statistiek"/>
                <div class="font-semibold mt-2">Langst in huidige fase</div>
                <el-table :data="page.langste" size="small">
                    <el-table-column prop="initiatief" label="Initiatief" min-width="260"/>
                    <el-table-column prop="organisatie" label="Organisatie" width="160"/>
                    <el-table-column label="Fase" width="120"><template #default="scope">{{ InitiatiefFaseEnum_name(scope.row.fase) }}</template></el-table-column>
                    <el-table-column prop="faseIngang" label="Fase-ingang" width="110"/>
                    <el-table-column prop="kwartalenInFase" label="Kwartalen in fase" width="140" align="right"/>
                    <el-table-column label="Impact FTE" width="110" align="right"><template #default="scope">{{ formatFte(scope.row.fte) }}</template></el-table-column>
                </el-table>
            </section>

            <section class="flex flex-col gap-2">
                <div class="text-xl font-semibold text-zzc-blue">Tijdlijn per initiatief</div>
                <div class="text-sm text-gray-500">Per rij een initiatief; de kleur per kwartaal is de fase. Lichte cellen zijn onderbroken (geen snapshot), een rode stip markeert een teruggezet initiatief.</div>
                <InitiatiefTimeline :rijen="page.tijdlijn" :kwartalen="page.kwartalen"/>
            </section>

            <section class="flex flex-col gap-2">
                <div class="text-xl font-semibold text-zzc-blue">Onderliggende initiatieven</div>
                <div class="flex flex-row flex-wrap items-center gap-4">
                    <el-select v-model="filterOrg" multiple collapse-tags placeholder="Organisatie" class="w-56">
                        <el-option v-for="o in organisaties" :key="o" :value="o" :label="o"/>
                    </el-select>
                    <el-select v-model="filterFase" multiple collapse-tags placeholder="Huidige fase" class="w-56">
                        <el-option v-for="f in INITIATIEF_FASE_VOLGORDE" :key="f" :value="f" :label="InitiatiefFaseEnum_name(f)"/>
                    </el-select>
                    <el-select v-model="filterBucket" multiple collapse-tags placeholder="FTE-klasse" class="w-48">
                        <el-option v-for="b in FTE_BUCKETS" :key="b" :value="b" :label="b"/>
                    </el-select>
                    <el-checkbox v-model="alleenUitschieters">Alleen uitschieters</el-checkbox>
                    <el-input v-model="zoek" placeholder="Zoeken" clearable class="w-64"/>
                    <span class="text-sm text-gray-500">{{ details.length }} regels</span>
                </div>
                <el-table :data="details" stripe size="small" max-height="600">
                    <el-table-column prop="organisatie" label="Organisatie" width="150"/>
                    <el-table-column prop="initiatief" label="Initiatief" min-width="260"/>
                    <el-table-column label="Huidige fase" width="120"><template #default="scope">{{ InitiatiefFaseEnum_name(scope.row.fase) }}</template></el-table-column>
                    <el-table-column prop="faseIngang" label="Fase-ingang" width="110"/>
                    <el-table-column prop="kwartalenInFase" label="Kwartalen in fase" width="140" align="right"/>
                    <el-table-column prop="bucket" label="FTE-klasse" width="100"/>
                    <el-table-column label="Uitschieter" width="100"><template #default="scope">{{ scope.row.uitschieter ? 'ja' : '' }}</template></el-table-column>
                    <el-table-column prop="toelichting" label="Toelichting" min-width="300" show-overflow-tooltip/>
                </el-table>
            </section>
        </template>
        <div v-else class="text-sm text-gray-500">Geen gegevens (laden mislukt).</div>
    </div>
</template>
